import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Button } from '../components/common/Button'
import { Card } from '../components/common/Card'
import { LoadingSpinner } from '../components/common/LoadingSpinner'
import { getMyRewards } from '../services/api'
import type { ClaimedReward } from '../types'

const TYPE_LABELS: Record<string, string> = {
  promo_code: 'Promo Code',
  badge: 'Badge',
  points: 'Points',
  message: 'Message',
  physical: 'Physical',
}

const TYPE_COLORS: Record<string, string> = {
  promo_code: 'bg-amber-100 text-amber-800',
  badge: 'bg-purple-100 text-purple-800',
  points: 'bg-blue-100 text-blue-800',
  message: 'bg-green-100 text-green-800',
  physical: 'bg-orange-100 text-orange-800',
}

function RewardCard({ reward }: { reward: ClaimedReward }) {
  const [copied, setCopied] = useState(false)

  const handleCopy = () => {
    if (!reward.code) return
    navigator.clipboard.writeText(reward.code)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <Card padding="md">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${TYPE_COLORS[reward.type] || 'bg-stone-100 text-stone-600'}`}>
              {TYPE_LABELS[reward.type] || reward.type}
            </span>
          </div>
          <h3 className="font-semibold text-brown-900">{reward.name}</h3>
          <p className="text-sm text-brown-500">{reward.hunt_title}</p>
          {reward.description && (
            <p className="text-sm text-brown-600 mt-1">{reward.description}</p>
          )}
          {reward.code && (
            <div className="mt-3 flex items-center gap-2">
              <span className="font-mono text-lg font-bold tracking-widest text-brown-900 bg-cream-100 border-2 border-dashed border-primary-300 rounded-lg px-4 py-2">
                {reward.code}
              </span>
              <button
                onClick={handleCopy}
                className={`p-2 rounded-lg transition-colors ${copied ? 'bg-green-100 text-green-600' : 'bg-stone-100 hover:bg-stone-200 text-stone-600'}`}
                title="Copy code"
              >
                {copied ? (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                )}
              </button>
            </div>
          )}
          <p className="text-xs text-brown-400 mt-2">
            Claimed {new Date(reward.claimed_at).toLocaleDateString()}
            {reward.expires_at && ` · Expires ${new Date(reward.expires_at).toLocaleDateString()}`}
          </p>
        </div>
      </div>
    </Card>
  )
}

export default function MyRewards() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['my-rewards'],
    queryFn: getMyRewards,
  })

  return (
    <div className="min-h-screen bg-cream-50">
      <header className="sticky top-0 z-10 bg-cream-50 border-b border-cream-200 px-4 py-4">
        <div className="max-w-lg mx-auto flex items-center gap-3">
          <Link to="/profile" className="w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-sm">
            <svg className="w-5 h-5 text-brown-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </Link>
          <h1 className="text-xl font-bold text-brown-900">My Rewards</h1>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 py-6">
        {isLoading && <LoadingSpinner />}

        {error && (
          <Card padding="md" className="text-center text-red-600">
            Failed to load rewards.
          </Card>
        )}

        {!isLoading && data?.rewards.length === 0 && (
          <div className="text-center py-16">
            <div className="text-5xl mb-4">🎁</div>
            <h2 className="text-lg font-semibold text-brown-900 mb-2">No rewards yet</h2>
            <p className="text-brown-600 mb-6">Complete hunts to earn promo codes and other rewards.</p>
            <Link to="/hunts">
              <Button>Browse Hunts</Button>
            </Link>
          </div>
        )}

        {data && data.rewards.length > 0 && (
          <div className="space-y-3">
            {data.rewards.map((r) => (
              <RewardCard key={r.id} reward={r} />
            ))}
          </div>
        )}
      </main>
    </div>
  )
}
